import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Icon,
    IconButton,
    styled,
    Tab,
    Tabs,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
} from '@mui/material';
import { GridActionsCellItem, GridCellParams, GridColDef, GridRowModel, GridRowParams } from '@mui/x-data-grid-pro';
import React from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { Observable } from 'rxjs';

import { PaginatedDataGrid } from 'app/components/paginated-data-grid';
import { SeamRequestLink } from 'app/components/seam-request-link';
import { TabPanel } from 'app/components/tab-panel';
import { Translate } from 'app/components/translate';
import { CrisRequestType } from 'app/constants/cris-request-type';
import { Cobrander } from 'app/model/cobrander';
import { KnpPaginationData } from 'app/model/knp-pagination-data';
import { ListOptions } from 'app/model/list-options';
import { ChannelPattern } from 'app/model/pre-prod/channel-pattern';
import { CorrectionPattern } from 'app/model/pre-prod/correction-pattern';
import { LocationPattern } from 'app/model/pre-prod/location-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { searchService } from 'app/services/search-service';
import { PreProdPattern } from 'app/type/pre-prod-pattern';

interface DeployedPatternsProps {
    request: SeamRequest;
    disableEdit?: boolean;

    onEdit?: (pattern: PreProdPattern) => void;
}

interface DeployedPatternsState {
    activePatternIds?: Record<CrisRequestType, number[]>;
    activeRequestMap?: Record<CrisRequestType, Record<number, string>>;
    loaded: boolean;
    tab: number;
}

const sharedColumns: GridColDef[] = [
    {
        field: 'cobrander',
        headerName: 'Cobrander',
        width: 120,
        valueFormatter: (params) => {
            const val = params.value as Cobrander | undefined;

            if (!val) {
                return undefined;
            }

            return `${val.name} (${val.id})`;
        },
    },
    { field: 'active', headerName: 'Active', width: 100, disableColumnMenu: true },
    { field: 'matchMethod', headerName: 'Method', width: 110, disableColumnMenu: true },
    { field: 'mtid', headerName: 'MTID', width: 120 },
    { field: 'mid', headerName: 'MID', width: 110 },
    { field: 'merchdesc', headerName: 'Descriptor', width: 250 },
    { field: 'mccOperator', headerName: 'MCC Op.', width: 80, disableColumnMenu: true },
    { field: 'mcc', headerName: 'MCC', width: 150 },
    { field: 'locationOperator', headerName: 'Location Operator', width: 130 },
    { field: 'city', headerName: 'City', width: 130 },
    { field: 'state', headerName: 'State', width: 90, disableColumnMenu: true },
    { field: 'zip', headerName: 'Zip', width: 90, disableColumnMenu: true },
];

const correctionColumns: GridColDef[] = [
    { field: 'action', headerName: 'Action', width: 100, disableColumnMenu: true },
    ...sharedColumns,
    { field: 'country', headerName: 'Country', width: 120, disableColumnMenu: true },
];

const channelColumns: GridColDef[] = [
    { field: 'subChannel', headerName: 'Channel', width: 110, disableColumnMenu: true },
    ...sharedColumns,
];

const locationColumns: GridColDef[] = [
    ...sharedColumns,
    { field: 'extractField', headerName: 'Ext Field', width: 130, disableColumnMenu: true },
    { field: 'extractPattern', headerName: 'Ext Pattern', width: 130, disableColumnMenu: true },
];

export class DeployedPatterns extends React.Component<DeployedPatternsProps, DeployedPatternsState> {
    public constructor(props: DeployedPatternsProps | Readonly<DeployedPatternsProps>) {
        super(props);

        this.state = {
            loaded: false,
            tab: 0,
        };
    }

    public componentDidMount(): void {
        this.setState({ loaded: false });
    }

    public render(): React.ReactNode {
        const store = this.props.request.store;
        if (!store) {
            return '';
        }

        return (
            <Accordion className='mt-1' onChange={() => this.onExpanded()}>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                    <Box mr={1}>
                        <Icon>layers</Icon>
                    </Box>
                    <Box flexGrow={1}>
                        <Typography>
                            <Translate tkey='patterns.expand.deployed' />
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box display='flex' flexDirection='column' flexGrow={1}>
                        <Box display='flex' flexDirection='row' alignItems='flex-end'>
                            <Tooltip title='Download Patterns'>
                                <IconButton color='primary' onClick={() => this.download()} size='large'>
                                    <Icon>file_download</Icon>
                                </IconButton>
                            </Tooltip>
                            <Box flexGrow={1}>
                                <Tabs
                                    value={this.state.tab}
                                    onChange={(evt, tab) => this.setState({ tab: tab as number })}
                                    indicatorColor='primary'
                                    textColor='primary'
                                    centered
                                >
                                    <Tab label='Stores' />
                                    <Tab label='Channels' />
                                    <Tab label='Store Numbers' />
                                </Tabs>
                            </Box>
                        </Box>

                        <TabPanel value={this.state.tab} index={0}>
                            {this.renderGrid(correctionColumns, (opts?: ListOptions) =>
                                searchService.findPatterns<CorrectionPattern>(CrisRequestType.STORE, store, opts),
                            )}
                        </TabPanel>
                        <TabPanel value={this.state.tab} index={1}>
                            {this.renderGrid(channelColumns, (opts?: ListOptions) =>
                                searchService.findPatterns<ChannelPattern>(CrisRequestType.CHANNEL, store, opts),
                            )}
                        </TabPanel>
                        <TabPanel value={this.state.tab} index={2}>
                            {this.renderGrid(locationColumns, (opts?: ListOptions) =>
                                searchService.findPatterns<LocationPattern>(CrisRequestType.LOCATION, store, opts),
                            )}
                        </TabPanel>
                    </Box>
                </AccordionDetails>
            </Accordion>
        );
    }

    private onExpanded() {
        const store = this.props.request.store;
        if (this.state.loaded || !store) {
            return;
        }

        searchService.findActivePatterns(store).subscribe((results) => {
            const activeRequestMap: Record<CrisRequestType, Record<number, string>> = {
                [CrisRequestType.CHANNEL]: {},
                [CrisRequestType.LOCATION]: {},
                [CrisRequestType.STORE]: {},
            };

            const activePatternIds: Record<CrisRequestType, number[]> = {
                [CrisRequestType.CHANNEL]: [],
                [CrisRequestType.LOCATION]: [],
                [CrisRequestType.STORE]: [],
            };

            results.forEach((item) => {
                activePatternIds[item.type].push(item.outputId);
                activeRequestMap[item.type][item.outputId] = item.id;
            });

            this.setState({ activePatternIds, activeRequestMap });
        });

        this.setState({ loaded: true });
    }

    private download() {
        const store = this.props.request.store;
        if (!this.state.loaded || !store) {
            return;
        }

        switch (this.state.tab) {
            case 0:
                searchService.downloadPatterns(CrisRequestType.STORE, store);
                break;
            case 1:
                searchService.downloadPatterns(CrisRequestType.CHANNEL, store);
                break;
            default:
                searchService.downloadPatterns(CrisRequestType.LOCATION, store);
        }
    }

    private prepareColumns(columns: GridColDef[]): GridColDef[] {
        const extraHeaders: GridColDef[] = [];

        extraHeaders.push({
            field: 'actions',
            headerName: 'Actions',
            type: 'actions',
            getActions: (params: GridRowParams<PreProdPattern>) => {
                const urlSearchParams = new URLSearchParams([
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    ...Object.entries({ ...params.row, store: this.props.request.store.id }).reduce<
                        Array<[string, string]>
                    >((arr, [key, value]) => {
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                if (val) {
                                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                    arr.push([key, `${val}`]);
                                }
                            });
                        } else if (value && 'object' !== typeof value) {
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                            arr.push([key, `${value}`]);
                        }

                        return arr;
                    }, []),
                ]);
                const renderData = this.shouldRenderEdit(params);

                return [
                    <Tooltip title='Open pattern in Tuple Explorer'>
                        <GridActionsCellItem
                            {...{
                                to: {
                                    pathname: '/explore/tuples',
                                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                    search: `?${urlSearchParams}`,
                                } satisfies LinkProps['to'],
                            }}
                            component={Link}
                            icon={<Icon>manage_search</Icon>}
                            label='Search'
                        />
                    </Tooltip>,
                    ...(!this.props.disableEdit && (renderData.canRender || renderData.reason)
                        ? [
                              renderData.canRender ? (
                                  <Tooltip title='Create replacement pattern'>
                                      <GridActionsCellItem
                                          onClick={() => {
                                              this.props.onEdit?.(params.row);
                                          }}
                                          icon={<Icon>edit</Icon>}
                                          label='Edit'
                                      />
                                  </Tooltip>
                              ) : (
                                  <HtmlTooltip title={renderData.reason} arrow>
                                      <Icon fontSize='small' color='info'>
                                          help_outline
                                      </Icon>
                                  </HtmlTooltip>
                              ),
                          ]
                        : []),
                ];
            },
        });

        extraHeaders.push({ field: 'id', headerName: 'ID', width: 90 });

        return [...extraHeaders, ...columns];
    }

    private renderGrid(
        columns: GridColDef[],
        onSearch: (options?: ListOptions) => Observable<KnpPaginationData<GridRowModel>>,
    ) {
        if (!this.state.loaded) {
            return '';
        }

        return (
            <PaginatedDataGrid
                columnVisibilityModel={{
                    mtid: false,
                }}
                columns={this.prepareColumns(columns)}
                autoHeight
                density='compact'
                dataSide={'server'}
                onSearch={onSearch}
            />
        );
    }

    private shouldRenderEdit(params: GridCellParams | { row: PreProdPattern }): {
        canRender: boolean;
        reason?: React.ReactNode;
    } {
        if (undefined === this.state.activePatternIds || undefined === this.state.activeRequestMap) {
            // we need to wait for this to load
            return { canRender: false };
        }

        const row = params.row as PreProdPattern;
        let isCorrection = false;
        if (Object.prototype.hasOwnProperty.call(row, 'action')) {
            isCorrection = 'UNMAP' === (row as CorrectionPattern).action;
        }

        if (isCorrection !== this.props.request.correction) {
            return { canRender: false };
        }

        // we provide reason only for special cases to avoid rendering useless info
        if (this.state.activePatternIds[row.type].includes(row.id)) {
            const seamId = this.state.activeRequestMap[row.type][row.id];

            return {
                canRender: false,
                reason: (
                    <Typography>
                        This pattern is active in another request <SeamRequestLink id={seamId} />
                    </Typography>
                ),
            };
        }

        if (!row.active) {
            return {
                canRender: false,
                reason: <Typography>This pattern is already disabled and as such should not be replaced</Typography>,
            };
        }

        return { canRender: true };
    }
}

const HtmlTooltip = styled(({ children, className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} children={children} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.grey['100'],
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 220,
        border: '1px solid #dadde9',
    },
}));
/* eslint max-lines: off */
